import {
    SWITCH_MAP,
    SWITCH_LIST,
    GET_PS_DATA,
    GET_DAM_DATA,
    SET_CURRENT_DAM,
    CLEAR_CURRENT_DAM,
    SET_MAP_ZOOM,
    SET_MAP_COORDINATES,
    SET_INFO_WINDOW_OPEN,
    SET_INFO_WINDOW_CLOSE,
    SET_ACTIVE_STATION,
    SET_DAM_ERROR,
    RESET_DASHBOARD_STATE,
    SET_MAP_EVENTS_VIEW_OPEN,
    SET_MAP_DATA_FILTER,
    CLEAR_MAP_DATA_FILTER,
    SET_PS_DATA_FILTER,
    GET_SHOW_SENSOR_ICONS,
    SET_SHOW_SENSOR_ICONS
} from "../actions/dashboard";
import {AUTH_LOGOUT} from "../actions/auth";
import {RESET_SENSORS_STATE} from "../actions/sensors";
import apiCall from "../../utils/api";
import i18n from "../../i18n";
import {
    getMeters,
    getSiteUniqueSensorsHelper,
    translateStatus,
    groupStatesByType,
    filterPsDataHelper
} from "../../utils/common";
import {psStatusFilters} from "../../constants/map-constants";

const tabs = {
    map: 'map',
    list: 'list'
};

const getDefaultState = () => {
    return {
        activeTab: tabs.map,
        psData: {},
        psNameFilter: '',
        psStatusFilters,
        damData: {},
        currentDam: null,
        currentDamError: false,
        psDict: [],
        damMapCoordinates: {
            lat: 42.65,
            lng: 25.25
        },
        mapZoom: 9,
        infoWindowOpened: false,
        activeStation: {},
        mapEventsViewOpen: false,
        showSensorIcons: false
    }
};

// initial state
const state = getDefaultState();

const getters = {
    isMapActive: state => state.activeTab === 'map',
    isListActive: state => state.activeTab === 'list',
    getPsData: state => state.psData,
    getFilteredPsData: state => filterPsDataHelper(state.psData, state.psNameFilter),
    getPsNameFilter: state => state.psNameFilter,
    getPsStatusFilters: state => state.psStatusFilters,
    getGroupedPsStates: state => groupStatesByType(state.psData.ps_state_dict || {}),
    currentDamSelected: state => {
        return state.currentDam !== null;
    },
    currentDamSelectedName: state => {
        if (state.currentDam === null) {
            return '';
        }
        if (i18n.locale && i18n.locale !== 'en') {
            return `${state.currentDam[6]}`;
        }
        return `${state.currentDam[0]}`;
    },
    currentDamSelectedId: state => {
        if (state.currentDam === null) {
            return '';
        }
        return `${state.currentDam[0]}_${state.currentDam[1]}`;
    },
    currentDamSelectedStatus: state => {
        if (state.currentDam && state.psData?.ps_state_dict) {
            const stateAsDigit = state.psData.ps_state_dict[`${state.currentDam[0]}_${state.currentDam[1]}`];
            return translateStatus(stateAsDigit);
        }
        if (state.currentDam && state.damData?.PS_station?.asList) {
            const stateAsDigit = state.damData.PS_station.asList[4];
            return translateStatus(stateAsDigit);
        }
        return [];
    },
    currentDamOverflowInfo: state => {
        if (!state.currentDam) {
            return {};
        }
        return state.currentDam[9];
    },
    currentDamSelectedCoordinates: state => {
        if (!state.currentDam) {
            return [];
        }
        return [state.currentDam[2], state.currentDam[3]];
    },
    currentDamSelectedLastUpdated: state => {
        if (!state.currentDam) {
            return '';
        }
        return state.currentDam[8];
    },
    currentDamError: state => {
        return state.currentDamError;
    },
    getNumberObjects: state => state.psData.PS_stations ? state.psData.PS_stations.length : 0,
    getDamData: state => state.damData.PS_station ? state.damData.PS_station : {},
    getDamIdFromData: state => state.damData ? `${state.damData.PS_name}_${state.damData.PS_index}` : '',
    getLastUpdatedObj: state => state.damData.PS_station ? state.damData.PS_station.lastUpdated : {},
    getDamPiezometers: state => {
        return getMeters('Piezometer', state.damData.PS_station);
    },
    getDamInclinometers: state => {
        return getMeters('Inclinometer', state.damData.PS_station);
    },
    getDamInclinometersNew: state => {
        if (state.damData.PS_station && state.damData.PS_station.asList) {
            return state.damData.PS_station.asList[5].Inclinometer;
        }
        
    },
    getDamLevelmeters: state => {
        return getMeters('Levelmeter', state.damData.PS_station);
    },
    getSiteUniqueSensors: state => {
        return getSiteUniqueSensorsHelper(state.damData.PS_station ? state.damData.PS_station : {});
    },
    getHasCamera: state => {
        if (state.damData.PS_station) {
            return state.damData.PS_station?.hasCamera;
        }
        return false;
    },
    getDamCoordinates: state => state.damMapCoordinates,
    getMapZoom: state => state.mapZoom,
    getInfoWindowOpened: state => state.infoWindowOpened,
    getActiveStation: state => state.activeStation,
    getAlarms: state => state.psData.alarms || [],
    getMapEventsViewOpen: state => state.mapEventsViewOpen,
    [GET_SHOW_SENSOR_ICONS]: state => state.showSensorIcons
};

const actions = {
    [SWITCH_MAP]: ({commit}) => {
        commit(SWITCH_MAP);
    },
    [SWITCH_LIST]: ({commit}) => {
        commit(SWITCH_LIST)
    },
    [SET_CURRENT_DAM]: ({commit, dispatch}, currentDamArr) => {
        commit(SET_CURRENT_DAM, currentDamArr);
        dispatch(RESET_SENSORS_STATE);
    },
    [CLEAR_CURRENT_DAM]: ({commit}) => {
        commit(CLEAR_CURRENT_DAM);
    },
    [GET_PS_DATA]: ({commit, dispatch}) => {
        return new Promise((resolve, reject) => {
            apiCall.getPsInfo()
                .then(resp => {
                    commit(GET_PS_DATA, resp);
                    resolve(resp.data);
                })
                .catch(err => {
                    console.log("ERROR in dashboard - get ps data");
                    if (err.request.status === 401) {
                        // if resp is unauthorized, logout
                        dispatch(AUTH_LOGOUT);
                    }
                    reject(err); // WARNING - must be properly handled by caller!
                });
        });
    },
    [GET_DAM_DATA]: ({commit, dispatch}, damName) => {
        return new Promise((resolve, reject) => {
            apiCall.getDamInfo(damName)
                .then(resp => {
                    commit(GET_DAM_DATA, resp);
                    resolve(resp.data);
                })
                .catch(err => {
                    console.log("ERROR in dashboard - get dam data");
                    if (err.request.status === 401) {
                        // if resp is unauthorized, logout
                        dispatch(AUTH_LOGOUT);
                    }
                    reject(err); // WARNING - must be properly handled by caller!
                });
        });
    },
    [SET_MAP_ZOOM]: ({commit}, zoom) => commit(SET_MAP_ZOOM, zoom),
    [SET_MAP_COORDINATES]: ({commit}, coordinates) => commit(SET_MAP_COORDINATES, coordinates),
    [SET_INFO_WINDOW_OPEN]: ({commit}) => commit(SET_INFO_WINDOW_OPEN),
    [SET_INFO_WINDOW_CLOSE]: ({commit}) => commit(SET_INFO_WINDOW_CLOSE),
    [SET_ACTIVE_STATION]: ({commit}, damData) => commit(SET_ACTIVE_STATION, damData),
    [SET_DAM_ERROR]: ({commit}, errorState) => commit(SET_DAM_ERROR, errorState),
    [RESET_DASHBOARD_STATE]: ({commit}) => commit(RESET_DASHBOARD_STATE),
    [SET_MAP_EVENTS_VIEW_OPEN]: ({commit}, open) => commit(SET_MAP_EVENTS_VIEW_OPEN, open),
    [SET_MAP_DATA_FILTER]: ({commit}, filters) => commit(SET_MAP_DATA_FILTER, filters),
    [CLEAR_MAP_DATA_FILTER]: ({commit}) => commit(CLEAR_MAP_DATA_FILTER),
    [SET_PS_DATA_FILTER]: ({commit}, filterStr) => commit(SET_PS_DATA_FILTER, filterStr),
    [SET_SHOW_SENSOR_ICONS]: ({commit}, newValue) => commit(SET_SHOW_SENSOR_ICONS, newValue)
};

const mutations = {
    [SWITCH_MAP]: state => {
        state.activeTab = tabs.map;
    },
    [SWITCH_LIST]: state => {
        state.activeTab = tabs.list;
    },
    [SET_CURRENT_DAM]: (state, currentDamArr) => {
        state.currentDam = currentDamArr;
    },
    [CLEAR_CURRENT_DAM]: (state) => {
        state.currentDam = null;
    },
    [GET_PS_DATA]: (state, resp) => {
        state.psData = resp.data;
    },
    [GET_DAM_DATA]: (state, resp) => {
        state.damData = resp.data;
    },
    [SET_MAP_DATA_FILTER]: (state, filters) => {
        state.psStatusFilters = filters;
    },
    [SET_MAP_ZOOM]: (state, zoom) => {
        state.mapZoom = zoom
    },
    [SET_MAP_COORDINATES]: (state, coordinates) => {
        state.damMapCoordinates = coordinates;
        state.infoWindowOpened = true;
    },
    [SET_INFO_WINDOW_OPEN]: (state) => {
        state.infoWindowOpened = true;
    },
    [SET_INFO_WINDOW_CLOSE]: (state) => {
        state.infoWindowOpened = false;
    },
    [SET_ACTIVE_STATION]: (state, damData) => {
        state.activeStation = damData
    },
    [SET_DAM_ERROR]: (state, errorState) => {
        state.currentDamError = errorState;
    },
    [RESET_DASHBOARD_STATE]: (state) => {
        Object.assign(state, getDefaultState());
    },
    [SET_MAP_EVENTS_VIEW_OPEN]: (state, open) => {
        state.mapEventsViewOpen = open
    },
    [CLEAR_MAP_DATA_FILTER]: (state) => {
        state.psStatusFilters = psStatusFilters;
    },
    [SET_PS_DATA_FILTER]: (state, filterStr) => {
        state.psNameFilter = filterStr;
    },
    [SET_SHOW_SENSOR_ICONS]: (state, newValue) => {
        state.showSensorIcons = newValue;
    }
};

export default {
    state,
    getters,
    actions,
    mutations
};